import { useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import type { CoachPoolWithMatchCount } from '@airreading/common/data';

import { DiscardCoachPoolChangesDialog } from './coach-pool-dialogs.js';
import { viewedTeacherPoolNote } from './teacher-pool-summary.js';
import { useCoachPoolMutation } from './use-coach-pool-mutation.js';

interface ViewedTeacherPoolProps {
  pool: CoachPoolWithMatchCount;
  isEditing?: boolean;
  hasUnsavedChanges?: () => boolean;
  onBack: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
}

function PoolStatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <Badge
      className={
        isActive
          ? 'border-transparent bg-green-100 text-green-700 hover:bg-green-100'
          : 'border-transparent bg-blue-100 text-blue-700 hover:bg-blue-100'
      }
    >
      {isActive ? 'Active' : 'Inactive'}
    </Badge>
  );
}

// Component on the main view when there is a coach pool selected
export const ViewedTeacherPool = ({
  pool,
  isEditing = false,
  hasUnsavedChanges = () => false,
  onBack,
  onEdit,
  onCancelEdit,
}: ViewedTeacherPoolProps) => {
  const { requestToggle, isUpdating, requestRemove, isRemoving, dialogs } = useCoachPoolMutation({
    onRemoved: onBack,
  });
  const pendingAction = useRef<(() => void) | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const headingId = `viewed-teacher-pool-${pool.id}-heading`;
  const activeAction = pool.isActive ? 'Deactivate' : 'Activate';
  const updating = isUpdating(pool.id);
  const removing = isRemoving(pool.id);

  const confirm = (action: () => void) => {
    if (hasUnsavedChanges()) {
      pendingAction.current = action;
      setDiscardOpen(true);
      return;
    }
    action();
  };

  const discardDialog = (
    <DiscardCoachPoolChangesDialog
      open={discardOpen}
      poolTitle={pool.title}
      onOpenChange={(open) => {
        setDiscardOpen(open);
        if (!open) {
          pendingAction.current = null;
        }
      }}
      onDiscard={() => {
        const action = pendingAction.current;
        pendingAction.current = null;
        setDiscardOpen(false);
        action?.();
      }}
    />
  );

  if (isEditing) {
    return (
      <>
        <div className="flex min-w-0 flex-1 items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id={headingId} className="text-base font-semibold">
                Editing {pool.title}
              </h2>
              <PoolStatusBadge isActive={pool.isActive} />
              <Badge variant="outline" className="bg-white font-normal">
                Unsaved changes
              </Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Adjust the name, description, or filters. The saved pool stays in effect until you confirm.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="dismiss"
              data-testid={`teachers-availabilities-viewed-pool-button-back-${pool.id}`}
              onClick={() => confirm(onBack)}
            >
              Back to all teachers
            </Button>
            <Button
              type="button"
              size="sm"
              variant="dismiss"
              aria-label={`${activeAction} ${pool.title}`}
              aria-busy={updating}
              disabled={updating}
              data-testid={`teachers-availabilities-viewed-pool-button-toggle-${pool.id}`}
              onClick={() =>
                confirm(() => {
                  onCancelEdit();
                  requestToggle(pool);
                })
              }
            >
              {activeAction}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="dismiss"
              data-testid={`teachers-availabilities-viewed-pool-button-cancel-${pool.id}`}
              onClick={() => confirm(onCancelEdit)}
            >
              Cancel
            </Button>
          </div>
        </div>
        {dialogs}
        {discardDialog}
      </>
    );
  }

  return (
    <>
      <div className="flex min-w-0 flex-1 items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id={headingId} className="text-xl font-semibold">
              {pool.title}
            </h2>
            <PoolStatusBadge isActive={pool.isActive} />
          </div>
          {pool.description?.trim() ? <p className="mt-1 text-sm">{pool.description.trim()}</p> : null}
          <p className="mt-1 text-sm text-muted-foreground">{viewedTeacherPoolNote(pool.matchingTeachersCount)}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
            aria-label={`Remove ${pool.title}`}
            aria-busy={removing}
            disabled={removing}
            data-testid={`teachers-availabilities-viewed-pool-button-remove-${pool.id}`}
            onClick={() => requestRemove(pool)}
          >
            Remove pool
          </Button>
          <Button
            type="button"
            size="sm"
            variant="dismiss"
            data-testid={`teachers-availabilities-viewed-pool-button-back-${pool.id}`}
            onClick={() => confirm(onBack)}
          >
            Back to all teachers
          </Button>
          <Button
            type="button"
            size="sm"
            variant="dismiss"
            aria-label={`${activeAction} ${pool.title}`}
            aria-busy={updating}
            disabled={updating}
            data-testid={`teachers-availabilities-viewed-pool-button-toggle-${pool.id}`}
            onClick={() => confirm(() => requestToggle(pool))}
          >
            {activeAction}
          </Button>
          <Button
            type="button"
            size="sm"
            data-testid={`teachers-availabilities-viewed-pool-button-edit-${pool.id}`}
            onClick={onEdit}
          >
            Edit pool
          </Button>
        </div>
      </div>
      {dialogs}
      {discardDialog}
    </>
  );
};
